// src/hooks/useSiteConfig.js
import { useState, useEffect, createContext, useContext } from 'react';
import { doc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Create Site Config Context
const SiteConfigContext = createContext();

// Custom hook to use site config context
export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

// Default site configuration
const defaultSiteConfig = {
  siteName: 'Winnepocket Landscaping',
  tagline: 'Professional Landscaping Services',
  description: 'Transform your outdoor space with professional landscaping services. From design to installation and maintenance, we bring your vision to life.',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  allowRegistration: true
};

// Site Config Provider Component
export const SiteConfigProvider = ({ children }) => {
  const [siteConfig, setSiteConfig] = useState(defaultSiteConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Initialize site config listener
  useEffect(() => {
    const siteConfigRef = doc(db, 'siteConfig', 'main');

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      siteConfigRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSiteConfig({
            ...defaultSiteConfig,
            ...data
          });
        } else {
          // Document doesn't exist, create it with defaults
          initializeSiteConfig();
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to site config:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Initialize site config document with defaults
  const initializeSiteConfig = async () => {
    try {
      const siteConfigRef = doc(db, 'siteConfig', 'main');
      await setDoc(siteConfigRef, {
        ...defaultSiteConfig,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('Site config initialized with defaults');
    } catch (err) {
      console.error('Error initializing site config:', err);
      setError('Failed to initialize site configuration');
    }
  };

  // Update site configuration
  const updateSiteConfig = async (updates, updatedBy = 'admin') => {
    setUpdating(true);
    setError(null);
    
    try {
      const siteConfigRef = doc(db, 'siteConfig', 'main');
      
      await updateDoc(siteConfigRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy
      });
      
      console.log('Site config updated successfully');
      return { success: true };
    } catch (err) {
      console.error('Error updating site config:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setUpdating(false);
    }
  };

  // Update a single setting
  const updateSetting = async (key, value, updatedBy = 'admin') => {
    return await updateSiteConfig({ [key]: value }, updatedBy);
  };

  // Reset to defaults
  const resetToDefaults = async (updatedBy = 'admin') => {
    setUpdating(true);
    setError(null);
    
    try {
      const siteConfigRef = doc(db, 'siteConfig', 'main');
      
      await updateDoc(siteConfigRef, {
        ...defaultSiteConfig,
        updatedAt: serverTimestamp(),
        updatedBy,
        resetAt: serverTimestamp()
      });
      
      console.log('Site config reset to defaults');
      return { success: true };
    } catch (err) {
      console.error('Error resetting site config:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setUpdating(false);
    }
  };

  const value = {
    // Site config data
    siteConfig,
    
    // Individual settings for easy access (removed requireEmailVerification)
    siteName: siteConfig.siteName,
    tagline: siteConfig.tagline,
    description: siteConfig.description,
    dateFormat: siteConfig.dateFormat,
    timeFormat: siteConfig.timeFormat,
    allowRegistration: siteConfig.allowRegistration,
    
    // State
    loading,
    error,
    updating,
    
    // Actions
    updateSiteConfig,
    updateSetting,
    resetToDefaults,
    
    // Computed properties
    isConfigLoaded: !loading && !error
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export default useSiteConfig;